import axios from "axios";
import { axiosInstance, axiosInstanceV2, axiosInstanceV3 } from "../axios";
import { RequestParameters, IModulesList, IPromotion, IActivity, PaginatedResponse } from "../types";
import { getNonPaginatedEndpointProd, getNonPaginatedEndpoint, getPaginatedEndpoint, snakeCaseDictToCamelCaseDict, urlWithParams } from "../utils";
import {
  IFaturasResponse,
  IContractResponse,
  IPromotionDetails,
  IHistory,
  ISales,
  ISale,
  INotificationItem,
  IMarkNotificationReadResponse,
  IMarkAllNotificationsReadResponse,
  IHasUnreadNotificationsResponse,
} from "./types";
import { IAgreement } from "../../interfaces/types";

export interface IAddress {
  street: string;
  country: string;
  city: string;
  state: string;
  neighborhood: string;
  cep: string;
  formattedAddress: string;
}

export const getState = () => {
  return axiosInstance.get('/states/').then(({ data }) => data);
};

export const getCity = (state = '') => {
  return axiosInstance.get(`/cities/?state=${state}`).then(({ data }) => data);
};

export function getLandingProd(slug: string, type: string = 'is_superapp'): Promise<IAgreement> {
  return getNonPaginatedEndpointProd(`/landings/?slug=${slug}&type=${type}`) as Promise<IAgreement>;
}

export function getLanding(slug: string, type: string = 'club'): Promise<IAgreement> {
  return getNonPaginatedEndpoint(`/landings/?slug=${slug}&type=${type}`) as Promise<IAgreement>;
}

export function getBenefits(params?: RequestParameters): Promise<IModulesList[]> {
  return axiosInstanceV2
    .get(`/agreement-modules/`)
    .then(({ data }) => data);
}

export function getProductsBenefits(params?: RequestParameters): Promise<IModulesList[]> {
  return axiosInstance
    .get(`/agreement-products/`, {
      params,
    })
    .then(({ data }) => data);
}
export function getNotifications(params?: RequestParameters): Promise<PaginatedResponse<INotificationItem>> {
  const paramsAux: RequestParameters = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value) {
      paramsAux[key] = value;
    }
  });

  return getPaginatedEndpoint<INotificationItem>(`/notifications/`, paramsAux, axiosInstanceV2);
}

export function markNotificationAsRead(id: number): Promise<IMarkNotificationReadResponse> {
  return axiosInstanceV2
    .patch(`/notifications/${id}/read/`)
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IMarkNotificationReadResponse);
}

export function markAllNotificationsAsRead(): Promise<IMarkAllNotificationsReadResponse> {
  return axiosInstanceV2
    .post(`/notifications/read-all/`)
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IMarkAllNotificationsReadResponse);
}

export function getHasUnreadNotifications(): Promise<IHasUnreadNotificationsResponse> {
  return axiosInstanceV2
    .get(`/notifications/has-unread/`)
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IHasUnreadNotificationsResponse);
}

export const CENTRAL_CLIENTE_PER_PAGE = 20;

export type CentralClientePageParams = {
  page?: number;
  per_page?: number;
};

function centralClienteParams(params?: CentralClientePageParams) {
  return {
    page: params?.page ?? 1,
    per_page: params?.per_page ?? CENTRAL_CLIENTE_PER_PAGE,
  };
}

export function getFaturas(params?: CentralClientePageParams): Promise<IFaturasResponse> {
  return axiosInstance.get(`/faturas/`, { params: centralClienteParams(params) }).then(({ data }) => data);
}

export function getContract(params?: CentralClientePageParams): Promise<IContractResponse> {
  return axiosInstance.get(`/contratos/`, { params: centralClienteParams(params) }).then(({ data }) => data);
}

export async function getPromotions(params?: RequestParameters): Promise<IPromotion[]> {
  const paramsAux: any = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value) {
      paramsAux[key] = value;
    }
  });
  const { results } = await getPaginatedEndpoint(`/promotion/promotion-list/`, paramsAux, axiosInstanceV3);
  return results as IPromotion[];
}

export async function getPromotionsPaginated(params?: RequestParameters): Promise<PaginatedResponse<IPromotion>> {
  const paramsAux: any = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value) {
      paramsAux[key] = value;
    }
  });
  return getPaginatedEndpoint<IPromotion>(`/promotion/promotion-list/`, paramsAux, axiosInstanceV3);
}

export function getPromotionDetails(id: string): Promise<IPromotionDetails> {
  return axiosInstanceV2
    .get(`/promotion/promotions/${id}/`)
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IPromotionDetails);
}

export function getDiscountDetails(id: string): Promise<IPromotionDetails> {
  return axiosInstance
    .get(`/discounts/${id}/`)
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IPromotionDetails);
}

export function generatePromotionCode(id: string, productId?: number): Promise<any> {
  const params = new URLSearchParams({ discount: id });
  if (productId) {
    params.append('product_id', productId.toString());
  }
  return axiosInstance.get(`/virtual_card/?${params.toString()}`);
}

export function getActivities(): Promise<IActivity[]> {
  return getNonPaginatedEndpoint(`/activities/`, {}, axiosInstanceV2) as Promise<IActivity[]>;
}

export function getCurrentAddressFromLatLong(latitude: number | string, longitude: number | string): Promise<IAddress> {
  return axios
    .get(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyB5tXrkiapXmzmvkkW0-7MQvfx1LL9VScQ`
    )
    .then((response) => {
      const address = response.data;
      const mappedAddress = {
        street: '',
        country: '',
        city: '',
        state: '',
        neighborhood: '',
        cep: '',
        formattedAddress: '',
      };

      if (!address || !Array.isArray(address.results) || address.results.length === 0) {
        throw new Error('Não foi possível parsear a localização');
      }

      mappedAddress.formattedAddress = address.results[0]?.formatted_address || '';

      return mappedAddress;
    });
}

export function toggleFavourite(promotionId: string, storeId?: string): Promise<any> {
  return axiosInstance.patch(`/profile/${promotionId}/toggle_favorite/`, {
    store_id: storeId,
  });
}

export async function getEconometer(): Promise<IHistory> {
  const { data } = await axiosInstance.get(`/profile/econometer/`);
  data.progress.reverse();
  return data;
}

export async function getSales(params: any): Promise<ISales[]> {
  return axiosInstanceV2
    .get(`/vouchers/my-vouchers`, {
      params,
    })
    .then(({ data }) => data);
}
export function getAgreementMagazines(agreement: number, params: any) {
  return axiosInstanceV2.get(`/magazine/magazines/?${(agreement && "&agreement=" + agreement) || ""}`, {
    params,
  }).then((result) => result.data.results);
}

export function createSale(data: ISale): Promise<any> {
  return axiosInstance.post('/sales/', data);
}

export function getCheckPermission(params: any) {
  return axiosInstance.post(urlWithParams('/profile/check_permission/', params));
}

export const removeAccount = () => {
  return axiosInstance.delete('/profile/delete_account/');
};

export function updateSubscription(id: number, data: any) {
  return axiosInstance.put(`/subscription/my-subscriptions/${id}/`, data);
}

export const sendPartnerEnquiry = (data: any) => {
  return axiosInstance.post('/enquiry/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
